import {
  CredentialExposure,
  environmentAllowsMaterializable,
  SECRET_ENVIRONMENTS,
  SecretEnvironment,
} from '@developerz/domain';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { PutRepoSecretsDotenvInput } from '../lib/repoSecrets';
import { Field } from './Field';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Add-secrets form for one repo (arc 101 slice 06, doc 06): environment +
// exposure pickers over a raw `.env` paste, mirroring `ConnectIntegrationModal`
// / `AddConnectionModal`. The paste lives ONLY in this local until the one
// submit call — it is never logged, persisted client-side, or echoed back.
// `dotenv` rides to the server verbatim; `parseDotenv` there is the one parser.
//
// Materializable is soft-disabled outside `dev` (`environmentAllowsMaterializable`)
// as a UX hint only — the server still refuses it (422) if this rule ever
// drifts, and that refusal surfaces verbatim via `props.error`.

export type AddRepoSecretModalProps = {
  readonly open: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (input: PutRepoSecretsDotenvInput) => void;
  readonly onClose: () => void;
};

export function AddRepoSecretModal(props: AddRepoSecretModalProps): JSX.Element {
  const [environment, setEnvironment] = createSignal<SecretEnvironment>(SecretEnvironment.Dev);
  const [exposure, setExposure] = createSignal<CredentialExposure>(CredentialExposure.Sealed);
  const [dotenv, setDotenv] = createSignal('');

  const materializableAllowed = (): boolean => environmentAllowsMaterializable(environment());

  // Reset on every open so a previous paste never bleeds into the next repo.
  createEffect(() => {
    if (props.open) {
      setEnvironment(SecretEnvironment.Dev);
      setExposure(CredentialExposure.Sealed);
      setDotenv('');
    }
  });

  // The environment changed under a materializable choice that no longer
  // qualifies — fall back rather than submit a batch the server will refuse.
  createEffect(() => {
    if (!materializableAllowed() && exposure() === CredentialExposure.Materializable) {
      setExposure(CredentialExposure.Sealed);
    }
  });

  const incomplete = (): boolean => dotenv().trim() === '';

  const submit = (e: Event): void => {
    e.preventDefault();
    if (incomplete()) return;
    props.onSubmit({ environment: environment(), exposure: exposure(), dotenv: dotenv() });
  };

  return (
    <Modal open={props.open} size="lg" title={t('repo_secrets.add_title')} onClose={props.onClose}>
      <form class="integration-form" onSubmit={submit}>
        <div class="field-group">
          <Field label={t('repo_secrets.environment_label')} id="repo-secret-environment">
            {(a11y) => (
              <Select
                a11y={a11y}
                label={t('repo_secrets.environment_label')}
                block
                value={environment()}
                options={SECRET_ENVIRONMENTS.map((env) => ({
                  value: env,
                  label: t(`repo_secrets.environment_${env}`),
                }))}
                onChange={setEnvironment}
              />
            )}
          </Field>
          <Field
            label={t('repo_secrets.exposure_label')}
            id="repo-secret-exposure"
            desc={
              materializableAllowed() ? undefined : t('repo_secrets.exposure_materializable_hint')
            }
          >
            {(a11y) => (
              <Select
                a11y={a11y}
                label={t('repo_secrets.exposure_label')}
                block
                value={exposure()}
                options={[
                  { value: CredentialExposure.Sealed, label: t('repo_secrets.exposure_sealed') },
                  {
                    value: CredentialExposure.Materializable,
                    label: t('repo_secrets.exposure_materializable'),
                    disabled: !materializableAllowed(),
                  },
                ]}
                onChange={setExposure}
              />
            )}
          </Field>
          <Field
            label={t('repo_secrets.dotenv_label')}
            id="repo-secret-dotenv"
            desc={t('repo_secrets.dotenv_desc')}
          >
            {(a11y) => (
              <textarea
                {...a11y}
                class="textarea"
                rows={8}
                value={dotenv()}
                onInput={(e) => setDotenv(e.currentTarget.value)}
                placeholder={t('repo_secrets.dotenv_placeholder')}
                autocomplete="off"
                spellcheck={false}
                required
              />
            )}
          </Field>
        </div>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('repo_secrets.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={props.busy || incomplete()}>
            {props.busy ? t('repo_secrets.adding') : t('repo_secrets.add_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
